/**
 * The sound an arriving message makes. Phase 72.
 *
 * Synthesised rather than played from a file, and that is a deliberate choice rather than a clever
 * one. A two-note chime is a few lines of Web Audio; an asset is a request, a cache entry, a thing
 * to host, and one more file to get wrong in a deployment. Nothing here needs decoding, so the
 * first notification is not the one that arrives late while an mp3 downloads.
 *
 * **Browsers refuse to play audio until somebody has interacted with the page.** Not a bug to work
 * around: it exists because pages that make noise before you have touched them are hostile. So the
 * context is created on the first click or key press this application sees, and until then a
 * notification simply does not chime — which is the correct behaviour for a tab nobody has touched.
 */

let context: AudioContext | null = null;
let unlocked = false;

/** The two notes. A rising pair reads as "something arrived" rather than as an alarm. */
const ARRIVED = [660, 880];

/** A single lower note: something happened, but not a customer waiting. */
const SOFT = [520];

function ensureContext(): AudioContext | null {
  if (context) return context;

  const Constructor =
    window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;

  if (!Constructor) return null;

  try {
    context = new Constructor();

    return context;
  } catch {
    // An audio context the browser refused to create is not worth a second attempt per sound.
    return null;
  }
}

/**
 * Called once, from the first real interaction.
 *
 * `resume()` is what turns a suspended context into one that will actually make a sound; calling it
 * from anywhere but a user gesture is what browsers reject.
 */
export function unlockSounds(): void {
  if (unlocked) return;

  const audio = ensureContext();

  if (!audio) return;

  unlocked = true;
  void audio.resume().catch(() => undefined);
}

function play(notes: readonly number[], volume: number): void {
  const audio = ensureContext();

  if (!audio || audio.state !== 'running') return;

  notes.forEach((frequency, index) => {
    const at = audio.currentTime + index * 0.12;
    const oscillator = audio.createOscillator();
    const gain = audio.createGain();

    // A sine is the one waveform that does not sound like a cheap alarm at low volume.
    oscillator.type = 'sine';
    oscillator.frequency.value = frequency;

    /*
     * An envelope, because a note that starts and stops instantly clicks.
     *
     * Ramped up over a few milliseconds and down exponentially, which is what makes it read as a
     * chime rather than as a beep.
     */
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(volume, at + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.22);

    oscillator.connect(gain).connect(audio.destination);
    oscillator.start(at);
    oscillator.stop(at + 0.24);
  });
}

/** A customer wrote. The one sound that should carry across a room. */
export function playMessageSound(): void {
  play(ARRIVED, 0.06);
}

/** Something happened that is worth knowing about and is not somebody waiting. */
export function playNotificationSound(): void {
  play(SOFT, 0.04);
}
